"use client";

import Image, { ImageProps } from "next/image";
import React, { useEffect, useState } from "react";
import { AppConstants } from "@/app/lib/util/AppConstants";
import { isRemoteImageUrl } from "@/app/lib/util/imageHosts";

const DEFAULT_AVATAR = AppConstants.DEFAULT_USER_AVATAR;

export const sanitizeImageSrc = (value?: string | null, fallback?: string) => {
  const trimmed = value?.trim();
  const resolvedFallback = fallback || DEFAULT_AVATAR;
  return trimmed && trimmed.length > 0 ? trimmed : resolvedFallback;
};

export const useSafeImageSrc = (
  src?: string | null,
  fallbackSrc = DEFAULT_AVATAR,
) => {
  const [safeSrc, setSafeSrc] = useState<string>(() =>
    sanitizeImageSrc(src, fallbackSrc),
  );
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
    const sanitized = sanitizeImageSrc(src, fallbackSrc);

    if (typeof window === "undefined") {
      setSafeSrc(sanitized);
      return;
    }

    if (!sanitized || sanitized === fallbackSrc) {
      setSafeSrc(sanitized);
      return;
    }

    let cancelled = false;
    const img = new window.Image();
    img.src = sanitized;
    img.onload = () => {
      if (!cancelled) {
        setSafeSrc(sanitized);
        setHasError(false);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setSafeSrc(fallbackSrc);
        setHasError(true);
      }
    };

    return () => {
      cancelled = true;
    };
  }, [src, fallbackSrc]);

  return { safeSrc: safeSrc || fallbackSrc, hasError };
};

type SafeImageProps = Omit<ImageProps, "src"> & {
  src?: string | null;
  fallbackSrc?: string;
  /** When true, render nothing if the image fails to load (instead of fallback) */
  hideOnError?: boolean;
};

const SafeImage: React.FC<SafeImageProps> = ({
  src,
  fallbackSrc = DEFAULT_AVATAR,
  hideOnError = false,
  style,
  ...rest
}) => {
  const { safeSrc, hasError } = useSafeImageSrc(src, fallbackSrc);
  const [imgError, setImgError] = useState(false);
  React.useEffect(() => setImgError(false), [src]);
  const displaySrc =
    hasError || imgError ? fallbackSrc : safeSrc || fallbackSrc;

  if (hideOnError && hasError) {
    return null;
  }

  // Preserve caller-defined dimensions (e.g. avatar circles). Only force auto-height
  // when no explicit width/height (or fill) is provided.
  const hasExplicitDimensions =
    typeof rest.width !== "undefined" && typeof rest.height !== "undefined";
  const usesFill = Boolean((rest as Partial<ImageProps>).fill);
  const aspectStyle: React.CSSProperties = {
    ...(style && typeof style === "object" && !Array.isArray(style)
      ? (style as React.CSSProperties)
      : {}),
    ...(hasExplicitDimensions || usesFill ? {} : { height: "auto" }),
  };

  if (isRemoteImageUrl(displaySrc)) {
    const { alt, className, width, height } = rest;
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={displaySrc}
        alt={alt ?? ""}
        className={className}
        width={width}
        height={height}
        style={aspectStyle}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <Image
      {...rest}
      src={displaySrc}
      style={aspectStyle}
      onError={() => setImgError(true)}
    />
  );
};

export default SafeImage;
